import { Box } from '@material-ui/core'
import { AllSearchResult, BackendUser, UserFeedback } from 'common/types'
import { SearchBar } from 'components/core/search'
import { useThemeSwitch } from 'hooks/theme'
import { useAnalytics } from 'hooks/use-analytics/useAnalytics'
import { useNotification } from 'hooks/use-notification/useNotification'
import { useNotifications } from 'hooks/use-notifications/useNotifications'
import { useProfilePercentage } from 'hooks/use-profile-percentage/useProfilePercentage'
import { useScreenSizes } from 'hooks/use-screen-sizes/useScreenSizes'
import { useUpdateModePreference } from 'hooks/use-update-mode-preference/useUpdateModePreference'

import { useAuth } from 'pages/auth/hooks/useAuth'
import { Dispatch, FunctionComponent, SetStateAction, useEffect, useLayoutEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useTheme } from 'styled-components'
import styled from 'styled-components/macro'
import { MainWrapperNavBar } from './MainWrapperNavBar'
import { Footer, FooterNav } from '../footer'
import { NavBar } from '../nav-bar'
import { useMainWrapper } from './hooks/useMainWrapper'

export const StyledPageWrapper = styled(Box)`
  && {
    display: flex;
    flex-direction: column;
    background-color: ${props => props.theme.colors.bgPrimary};
    position: relative;
    width: 100%;
    height: 100%;
  }
`

export const StyledMain = styled(({ isDevice, ...props }) => <Box {...props} />)<{ isDevice?: boolean }>`
  && {
    width: 100%;
    height: 100%;
    padding-top: 70px;
    padding-bottom: ${props => (props?.isDevice ? '70px' : '90px')};
  }
`

export interface IMainWrapperProps {
  title: string
  shrinkLogo?: boolean
  hideHeader?: boolean
  stickTopBar?: boolean
  hideActions?: boolean
  user?: BackendUser
  showAvatar?: boolean
  hideFooter?: boolean
  nopadding?: boolean
  hideLinks?: boolean
  showSearch?: boolean
  inviteUsersByEmail?: Function
  stickyFooter?: boolean
  nomargin?: boolean
  setSearchTab?: Dispatch<SetStateAction<number>>
  setPageNum?: Dispatch<SetStateAction<number>>
  setResults?: Dispatch<SetStateAction<AllSearchResult[]>>
  isPostingContent?: boolean
}

export const MainWrapper: FunctionComponent<IMainWrapperProps> = (props) => {
  const {
    title,
    shrinkLogo,
    hideHeader,
    stickTopBar,
    hideActions,
    children,
    user,
    showAvatar,
    hideFooter,
    nopadding,
    nomargin,
    hideLinks,
    showSearch,
    inviteUsersByEmail,
    stickyFooter,
    setSearchTab,
    setPageNum,
    setResults,
    isPostingContent
  } = props
  const {
    backendUser,
    backendPartner,
    isDevice,
    isTablet,
    getProfilePercentage,
    logEventAnalytics,
    canSearch
  } = useMainWrapper(props)

  useLayoutEffect(() => {
    document.title = `${title} | Obsidi`
  }, [title])

  useEffect(() => {
    document.body.style.overflow = 'hidden auto'

    return () => {
      document.body.style.overflow = ''
    }
  }, [])

  return (
    <StyledPageWrapper width="100%" height="100%" id="wrapper">
      {!hideHeader && backendUser ? (
        <MainWrapperNavBar
          shrinkLogo={shrinkLogo}
          stickTopBar={stickTopBar}
          hideActions={hideActions}
          showAvatar={showAvatar}
          hideLinks={hideLinks}
          isPostingContent={isPostingContent}
          showSearch={showSearch}
          setSearchTab={setSearchTab}
          setPageNum={setPageNum}
          setResults={setResults}
          inviteUsersByEmail={inviteUsersByEmail}
        />
      ) : (
        <NavBar
          inviteUsersByEmail={inviteUsersByEmail}
          backendUser={backendUser}
          getProfilePercentage={getProfilePercentage(backendUser)}
          isFixed
        />
      )}

      <StyledMain isDevice={isDevice}>
        {canSearch && (
          <Box display="flex" alignItems="center" justifyContent="center" height="60px" pt="16px">
            <Box display="block">
              <SearchBar
                backendUser={backendUser}
                isDevice={isDevice}
                isTablet={isTablet}
                setSearchTab={setSearchTab}
                setResults={setResults}
                setPageNum={setPageNum}
              />
            </Box>
          </Box>
        )}
        <Box width="100%" height={canSearch ? 'calc(100% - (60px + 16px))' : '100%'} pt="16px">
          {children}
        </Box>
      </StyledMain>

      {!hideFooter && !isDevice && (
        <Box
          display="flex"
          width="100%"
          justifyContent="center"
          position={stickyFooter ? 'fixed' : 'unset'}
          bottom={stickyFooter ? '0' : ''}
          onClick={() => {
            const partnerLog = {}
            const userLog = {}

            userLog['User ID'] = backendUser?.id

            if (backendUser?.partnerId) {
              partnerLog['Partner ID'] = backendPartner?.id
              partnerLog['Company Name'] = backendPartner?.name
              partnerLog['Account Type'] = 'Partner'
            } else {
              userLog['Account Type'] = backendUser?.role
            }
            logEventAnalytics(
              backendUser?.partnerId ? 'Partner' : 'Member',
              'Home page view by Footer',
              'mixpanel',
              'Home page view by Footer',
              { ...partnerLog, ...userLog },
              backendUser?.id
            )
          }}
        >
          <Footer backendUser={backendUser} />
        </Box>
      )}
      {!hideFooter && isDevice && <FooterNav user={backendUser} />}
    </StyledPageWrapper>
  )
}
